import type {
  BuybackOffer,
  DiagnosticPayload,
} from '@backmarket/http-api/src/api-models/BuybackOffer'
import type { Currency } from '@backmarket/http-api/src/standards/Currency'
import type { I18n } from '@backmarket/nuxt-module-i18n/types/i18n'

import { toBuybackOrderCheckup } from '../../../../utils/adapters'
import type {
  GetOfferResponseLegacy,
  GetOfferV1Response,
} from '../../api/types'

function toCartDiagnosticPayload(
  rawCheckup: GetOfferV1Response['rawCheckup'],
  queries: Record<string, unknown> | null,
): GetOfferResponseLegacy['diagnostic_payload'] {
  return {
    // Used for tracking
    brand: queries?.brand,
    category: queries?.category,
    model: queries?.model,
    // Used for creating a trade-in order
    esthetic_condition:
      rawCheckup.estheticCondition ?? queries?.esthetic_condition,
    state_screen: rawCheckup.screenCondition ?? queries?.stateScreen,
    state_body: rawCheckup.casingCondition ?? queries?.stateBody,
    state_functional: rawCheckup.workingCondition ?? queries?.stateFunctional,
    battery: rawCheckup.batteryCondition ?? queries?.battery,
  }
}

export function buybackOfferToLegacyOffer(
  offer: GetOfferV1Response,
  queries: Record<string, unknown> | null,
  currencySign: I18n['currencySign'],
): GetOfferResponseLegacy {
  const date = new Date()
  const currency = offer.listing.price.currency as Currency

  return {
    title: offer.product.fullTitle,
    thumbnail: offer.product.image,
    price: Number(offer.listing.price.amount),
    price_with_currency: `${offer.listing.price.amount.replace('.', ',')} ${currencySign(currency)}`,
    standard_price: {
      amount: offer.listing.price.amount,
      currency,
    },
    sourcing_listing_public_id: offer.listing.listingPublicId,
    date_created: date.toISOString().slice(0, 19).replace('T', ' '),
    wording: offer.instructions,
    diagnostic_summary: offer.checkup,
    diagnostic_payload: toCartDiagnosticPayload(offer.rawCheckup, queries),
    has_shipping_choices: offer.shipping.hasChoice,
    hasBankDetails: offer.bankDetailsRequired,
    defaultShippingModeId: offer.shipping.default,
    tier: offer.tier,
  }
}

export function offerV1ToBuybackOffer(
  offer: GetOfferV1Response,
  queries: Record<string, unknown> | null,
  currencySign: I18n['currencySign'],
): BuybackOffer {
  const legacy = buybackOfferToLegacyOffer(offer, queries, currencySign)

  return {
    title: legacy.title,
    thumbnail: legacy.thumbnail,
    standardPrice: legacy.standard_price,
    price: legacy.price,
    priceWithCurrency: legacy.price_with_currency,
    // Numeric listing ID is not available in GetOfferV1Response
    sourcingListing: 0,
    dateCreated: legacy.date_created,
    hasShippingChoices: legacy.has_shipping_choices,
    hasBankDetails: legacy.hasBankDetails,
    wording: legacy.wording,
    diagnosticSummary: legacy.diagnostic_summary,
    // toBuybackOrderCheckup (called by consumers) runs camelizeKeys internally,
    // so the snake_case payload produced by buybackOfferToLegacyOffer is safe here.
    diagnosticPayload: legacy.diagnostic_payload as DiagnosticPayload,
    defaultShippingModeId: legacy.defaultShippingModeId,
    sourcingListingPublicId: legacy.sourcing_listing_public_id,
  }
}

export function legacyOfferToBuybackOffer(
  offer: GetOfferResponseLegacy,
): GetOfferV1Response {
  return {
    product: {
      fullTitle: offer.title,
      image: offer.thumbnail,
      // We cannot retrieve this from legacy offer
      productId: '',
      shortTitle: offer.title,
    },
    listing: {
      price: {
        amount: offer.standard_price.amount,
        currency: offer.standard_price.currency,
      },
      listingPublicId: offer.sourcing_listing_public_id,
    },
    instructions: offer.wording,
    checkup: offer.diagnostic_summary,
    shipping: {
      hasChoice: offer.has_shipping_choices,
      default: offer.defaultShippingModeId,
    },
    bankDetailsRequired: offer.hasBankDetails,
    tier: offer.tier,
    grade: 'FUNCTIONAL_FLAWLESS',
    funnelSummary: [],
    // Cast is legit as diagnostic_payload was originally populated
    // in buybackOfferToLegacyOffer with rawCheckup, brand, category, and model,
    // so the shape is known at runtime.
    rawCheckup: toBuybackOrderCheckup(
      offer.diagnostic_payload as DiagnosticPayload,
    ),
  }
}
